import type { Session } from "@supabase/supabase-js";

const SESSION_KEY = "heyea.supabase.session";

let memorySession: Session | null = null;

/**
 * Loads session from Office.context.roamingSettings.
 * Returns cached in-memory session if available.
 * Does not validate expiration - that's auth-service's responsibility.
 */
export async function loadSession(): Promise<Session | null> {
  // Return cached session if available
  if (memorySession && isSessionValid(memorySession)) {
    return memorySession;
  }

  try {
    // Office.context.roamingSettings is synchronous, but we keep async for API compatibility
    const stored = Office.context.roamingSettings.get(SESSION_KEY);
    
    if (stored && isSessionValid(stored)) {
      memorySession = stored as Session;
      console.log("[SessionStore] Loaded session from roamingSettings", {
        userId: memorySession.user?.id,
        email: memorySession.user?.email,
        expiresAt: memorySession.expires_at,
        hasRefreshToken: !!memorySession.refresh_token,
        expiresAtDate: memorySession.expires_at 
          ? new Date(memorySession.expires_at * 1000).toISOString() 
          : null,
      });
      return memorySession;
    }

    console.log("[SessionStore] No valid session found in roamingSettings");
    return null;
  } catch (err) {
    console.error("[SessionStore] load failed", err);
    return null;
  }
}

/**
 * Saves session to both memory and Office.context.roamingSettings.
 * Calls saveAsync to persist. Treats saveAsync failures as non-fatal.
 */
export async function saveSession(session: Session): Promise<void> {
  memorySession = session;
  
  try {
    Office.context.roamingSettings.set(SESSION_KEY, session);
    
    // Persist to server - this is async and may fail
    await new Promise<void>((resolve, reject) => {
      Office.context.roamingSettings.saveAsync((asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          console.log("[SessionStore] Session saved to roamingSettings");
          resolve();
        } else {
          // Non-fatal error per spec - log and continue
          console.error("[SessionStore] saveAsync failed (non-fatal):", asyncResult.error);
          resolve(); // Resolve anyway to continue operation
        }
      });
    });
  } catch (err) {
    // Non-fatal error - session is in memory
    console.error("[SessionStore] saveSession failed (non-fatal):", err);
  }
}

/**
 * Clears session from both memory and Office.context.roamingSettings.
 */
export async function clearSession(): Promise<void> {
  memorySession = null;
  
  try {
    Office.context.roamingSettings.remove(SESSION_KEY);
    
    await new Promise<void>((resolve) => {
      Office.context.roamingSettings.saveAsync((asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          console.log("[SessionStore] Session cleared from roamingSettings");
        } else {
          console.error("[SessionStore] saveAsync failed during clear:", asyncResult.error);
        }
        resolve(); // Always resolve
      });
    });
  } catch (err) {
    console.error("[SessionStore] clearSession failed:", err);
  }
}

/**
 * Validates that a session has the minimum required fields.
 * Per spec: only checks for refresh_token presence.
 */
export function isSessionValid(session: Session | null | undefined): session is Session {
  if (!session) {
    return false;
  }
  
  // Must have access_token and refresh_token per spec
  const accessOk = typeof session.access_token === "string" && session.access_token.length > 0;
  const refreshOk = typeof session.refresh_token === "string" && session.refresh_token.length > 0;
  
  return accessOk && refreshOk;
}

/**
 * Returns the in-memory cached session without loading from storage.
 */
export function getCachedSession(): Session | null {
  return memorySession;
}

/**
 * Sets the in-memory cached session.
 */
export function setCachedSession(session: Session | null): void {
  memorySession = session;
}

/**
 * Returns the current time in seconds since Unix epoch.
 */
export function nowEpochSec(): number {
  return Math.floor(Date.now() / 1000);
}

/**
 * Extracts the access token expiration time from a session.
 * Prefers the session's expires_at field.
 * Falls back to decoding the JWT payload to read the exp claim.
 */
export function getAccessTokenExpEpochSec(session: Session): number | null {
  // Prefer expires_at if present
  if (typeof session.expires_at === "number" && session.expires_at > 0) {
    return session.expires_at;
  }

  // Fallback: decode JWT payload
  if (session.access_token) {
    try {
      const parts = session.access_token.split(".");
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        if (typeof payload.exp === "number" && payload.exp > 0) {
          return payload.exp;
        }
      }
    } catch {
      // Invalid JWT format
    }
  }

  return null;
}

/**
 * Checks if the access token is expired.
 * Includes a skew buffer (default 60 seconds).
 */
export function isAccessTokenExpired(session: Session, skewSec = 60): boolean {
  const expEpochSec = getAccessTokenExpEpochSec(session);
  if (expEpochSec === null) {
    return true;
  }
  return expEpochSec <= nowEpochSec() + skewSec;
}

/**
 * Determines if the access token should be refreshed proactively.
 * Default buffer is 5 minutes (300 seconds) before expiration.
 */
export function shouldRefreshAccessToken(session: Session, refreshSkewSec = 300): boolean {
  const expEpochSec = getAccessTokenExpEpochSec(session);
  if (expEpochSec === null) {
    return true;
  }
  return expEpochSec <= nowEpochSec() + refreshSkewSec;
}
